import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timers = useRef(new Map());
  const entries = useRef([]);
  const remove = useCallback(id => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    entries.current = entries.current.filter(toast => toast.id !== id);
    setToasts(entries.current);
  }, []);
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const show = useCallback((message, { type = 'info', duration } = {}) => {
    const existing = entries.current.find(toast => toast.message === message && toast.type === type);
    const id = existing?.id || ++idRef.current;
    clearTimeout(timers.current.get(id));
    const next = [...entries.current.filter(toast => toast.id !== id), { id, message, type }];
    next.slice(0, -3).forEach(toast => { clearTimeout(timers.current.get(toast.id)); timers.current.delete(toast.id); });
    entries.current = next.slice(-3);
    setToasts(entries.current);
    if (type === 'success') window.dispatchEvent(new CustomEvent('bb:toast'));
    const lifetime = duration ?? (type === 'error' ? 7000 : 3000);
    if (lifetime) timers.current.set(id, setTimeout(() => remove(id), lifetime));
    return id;
  }, [remove]);
  const value = useMemo(() => ({ show }), [show]);
  return <ToastContext.Provider value={value}>
    {children}
    <div className="pointer-events-none fixed inset-x-0 top-[calc(1rem+env(safe-area-inset-top,0px))] z-[1300] flex flex-col items-center gap-2 px-4">
      {toasts.map(toast => {
        const Icon = toast.type === 'success' ? CheckCircle2 : toast.type === 'error' ? AlertCircle : Info;
        return <div key={toast.id} className="bb-toast pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-white px-4 py-2 text-sm text-zinc-900 shadow-lg ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-white dark:ring-zinc-700">
          <Icon aria-hidden="true" className={`h-5 w-5 shrink-0 ${toast.type === 'error' ? 'text-red-600 dark:text-red-400' : toast.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-primary'}`} />
          <span role={toast.type === 'error' ? 'alert' : 'status'} className="min-w-0 flex-1 break-words">{toast.message}</span>
          <button type="button" aria-label="Melding sluiten" onClick={() => remove(toast.id)} className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-xl text-zinc-500 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-zinc-400 dark:hover:bg-zinc-800"><X className="h-4 w-4" aria-hidden="true" /></button>
        </div>;
      })}
    </div>
  </ToastContext.Provider>;
}
